// src/hooks/useCockpitData.ts
// ============================================
// Cockpit 数据聚合 Hook
//
// 职责：
//   1. 集中拉取驾驶舱所需的所有实时数据
//   2. 不同数据源采用不同轮询周期（KPI 3s / 活动 5s / 性能 5s / 其他 30s）
//   3. 真实接入 RuntimeManager（WorkflowRuntime / SchedulerRuntime / 状态总览）
//   4. 失败时降级到 mock 数据，控制台告警 [DEGRADED]
//
// 数据源：
//   - KPI: RuntimeManager.getStatusSummary() + WorkflowRuntime.query(ListExecutions)
//   - Runtimes: RuntimeManager.getStatusSummary() + getLatencyStats()
//   - Activities: WorkflowRuntime.query(ListExecutions)（取最近 20 条）
//   - WorkflowDag: WorkflowRuntime.query(GetExecution)（最近一条执行实例）
//   - Performance: 模拟最近 1 小时折线数据（暂无聚合接口）
//   - Todos: SchedulerRuntime.query(List)
// ============================================

import { useEffect, useRef, useState } from "react";
import RuntimeManager from "../runtime/RuntimeManager";
import { RuntimeName } from "../types/runtime";
import type {
  RuntimeHealth,
  RuntimeResponse,
  RuntimeStatus as RuntimeStatusType,
} from "../types/runtime";
import { WorkflowQuery, WorkflowStatus } from "../runtime/WorkflowRuntime/types";
import type { WorkflowExecution } from "../runtime/WorkflowRuntime/types";
import { SchedulerQuery } from "../runtime/SchedulerRuntime/types";
import type { ScheduledTask } from "../runtime/SchedulerRuntime/types";
// v6.3.24 活动事件总线（系统真实事件，localStorage 持久化）
import { activityBus, type StreamEvent } from "../runtime/ActivityBus";

// ============================================
// 类型定义
// ============================================

/** KPI 趋势数据 */
export interface KpiTrend {
  /** 与上一次取值的百分比变化（正=上升） */
  delta: number;
  /** mini sparkline 数据（最近 12 个点） */
  sparkline: number[];
}

/** 4 个核心 KPI */
export interface KpiData {
  /** 活跃工作流数 */
  activeWorkflows: number;
  /** 执行成功率（0-100） */
  successRate: number;
  /** 平均执行时长（毫秒） */
  avgDuration: number;
  /** 系统健康度（0-100，14 Runtime 加权平均） */
  healthScore: number;
  /** 趋势 */
  trends: {
    activeWorkflows: KpiTrend;
    successRate: KpiTrend;
    avgDuration: KpiTrend;
    healthScore: KpiTrend;
  };
}

/** 单个 Runtime 状态条目 */
export interface RuntimeTile {
  name: RuntimeName;
  status: RuntimeStatusType;
  health: RuntimeHealth;
  version?: string;
  /** 调用次数（来自 LatencyStats） */
  calls: number;
  /** 平均延迟（毫秒） */
  avgLatency: number;
  /** 运行时长（秒） */
  uptime: number;
}

/** 活动事件类型 */
export type ActivityEventType =
  | "workflow.started"
  | "workflow.completed"
  | "workflow.failed"
  | "agent.thinking"
  | "knowledge.query"
  | "memory.recorded"
  | "notification.sent"
  // v6.3.24 系统事件（活动总线）
  | "system.ready"
  | "system.info";

/** 活动事件状态色 */
export type ActivityStatus = "running" | "success" | "failed" | "warning" | "info";

/** 单条活动事件 */
export interface ActivityItem {
  id: string;
  type: ActivityEventType;
  status: ActivityStatus;
  summary: string;
  /** ISO 时间字符串 */
  timestamp: string;
  /** 来源 Runtime */
  source?: RuntimeName;
}

/** 性能数据点 */
export interface PerformancePoint {
  /** ISO 时间字符串 */
  time: string;
  /** 显示用时间标签（HH:mm） */
  label: string;
  /** 成功执行时长（毫秒） */
  success: number;
  /** 失败执行时长（毫秒） */
  failed: number;
}

/** 待办项 */
export interface TodoItem {
  id: string;
  title: string;
  /** ISO 时间字符串 */
  time: string;
  priority: "high" | "medium" | "low";
  completed: boolean;
}

/** Cockpit 完整数据集 */
export interface CockpitData {
  kpi: KpiData;
  runtimes: RuntimeTile[];
  activities: ActivityItem[];
  workflowDag: WorkflowExecution | null;
  performance: PerformancePoint[];
  todos: TodoItem[];
  /** 是否处于降级模式（部分或全部数据为 mock） */
  degraded: boolean;
  /** 是否首次加载中 */
  loading: boolean;
  /** 最后更新时间 */
  lastUpdated: string | null;
}

// ============================================
// Runtime 顺序（用于 RuntimeMatrix 渲染）
// 说明：前 14 个为基础 Runtime（保留原顺序），
//       后续项按 src/runtime/register.ts 中 registerRuntime 无条件注册的顺序追加。
//       仅收录确定会注册的 Runtime，排除默认关闭的 Experience / AgentMesh，
//       以及仅存在于枚举未实际注册的 ContentPipeline / BusinessCore（骨架占位）。
// ============================================
const RUNTIME_ORDER: RuntimeName[] = [
  // —— 基础 14 个（原名顺序）——
  RuntimeName.Hermes,
  RuntimeName.Quant,
  RuntimeName.Knowledge,
  RuntimeName.Business,
  RuntimeName.Content,
  RuntimeName.Workflow,
  RuntimeName.Agent,
  RuntimeName.Memory,
  RuntimeName.Enterprise,
  RuntimeName.Integration,
  RuntimeName.Notification,
  RuntimeName.Scheduler,
  RuntimeName.Prompt,
  RuntimeName.Tool,
  // —— 策略级（Program B：GEO+DSO 等，register.ts 优先注册）——
  RuntimeName.GEO,
  RuntimeName.DSO,
  RuntimeName.GrowthFactory,
  RuntimeName.GEODiagnostic,
  // —— P2-2 / P2-5 / P2-3 新增——
  RuntimeName.LLM,
  RuntimeName.Skill,
  RuntimeName.Workspace,
  // —— Sprint 1 内容生产流水线——
  RuntimeName.Calendar,
  RuntimeName.Editorial,
  RuntimeName.VideoPipeline,
  // —— Sprint 2 知识图谱 / CEO / Sales——
  RuntimeName.KnowledgeGraph,
  RuntimeName.CEO,
  RuntimeName.Sales,
  // —— Sprint 4 企业知识中台 / 内容工厂——
  RuntimeName.CRM,
  RuntimeName.SKU,
  RuntimeName.SOP,
  RuntimeName.ContentFactory,
  RuntimeName.ContentCalendar,
  RuntimeName.TopicEngine,
  RuntimeName.BrandLibrary,
  RuntimeName.ContentLifecycle,
  // —— Sprint 3 新闻中心 / Project——
  RuntimeName.News,
  RuntimeName.Project,
  // —— 企业决策 / 运营 / 增长 / 自动驾驶——
  RuntimeName.BusinessEngine,
  RuntimeName.BusinessOperator,
  RuntimeName.EnterpriseBrain,
  RuntimeName.GrowthEngine,
  RuntimeName.Autopilot,
  // —— Program A 产品 Runtime——
  RuntimeName.Hotel,
  RuntimeName.GoldFactory,
  RuntimeName.AIDestiny,
  RuntimeName.MarketingOS,
  // —— Program C 收入引擎 / Phase D 媒体工作室——
  RuntimeName.RevenueEngine,
  RuntimeName.MediaStudio,
  // —— 知识演化 / 内容策略 / Coze 爆款分析——
  RuntimeName.KnowledgeEvolution,
  RuntimeName.ContentStrategy,
  RuntimeName.CozeWorkflowGen,
  RuntimeName.CozeExecutor,
];

// ============================================
// 空数据生成器（降级/未就绪时使用）
// 白皮书铁律一：禁止 mock 假数据 —— 数据源不可用时返回空结构，UI 显示「暂无」
// ============================================

const EMPTY_KPI: KpiData = {
  activeWorkflows: 0,
  successRate: 0,
  avgDuration: 0,
  healthScore: 0,
  trends: {
    activeWorkflows: { delta: 0, sparkline: [] },
    successRate: { delta: 0, sparkline: [] },
    avgDuration: { delta: 0, sparkline: [] },
    healthScore: { delta: 0, sparkline: [] },
  },
};

/** 空 KPI（数据源不可用时返回，绝不填充假数字） */
function emptyKpi(): KpiData {
  return {
    ...EMPTY_KPI,
    trends: {
      activeWorkflows: { delta: 0, sparkline: [] },
      successRate: { delta: 0, sparkline: [] },
      avgDuration: { delta: 0, sparkline: [] },
      healthScore: { delta: 0, sparkline: [] },
    },
  };
}

/** 空 Runtime 列表 */
function emptyRuntimes(): RuntimeTile[] {
  return [];
}

/** 空活动流 */
function emptyActivities(): ActivityItem[] {
  return [];
}

/** 空性能数据 */
function emptyPerformance(): PerformancePoint[] {
  return [];
}

/** 空待办列表 */
function emptyTodos(): TodoItem[] {
  return [];
}

// ============================================
// 工具函数
// ============================================

/** 安全读取 RuntimeResponse.data 并确保返回数组（避免 .filter/.map 等调用报错） */
function unwrapArray<T>(resp: RuntimeResponse): T[] {
  if (resp.success && Array.isArray(resp.data)) {
    return resp.data as T[];
  }
  return [];
}

/** 计算健康度（0-100） */
function computeHealthScore(
  health: Partial<Record<RuntimeName, RuntimeHealth>>
): number {
  const values = Object.values(health);
  if (values.length === 0) return 0;
  const score = values.reduce((acc, h) => {
    switch (h) {
      case "healthy":
        return acc + 100;
      case "degraded":
        return acc + 60;
      case "unhealthy":
        return acc + 10;
      default:
        return acc + 30; // unknown
    }
  }, 0);
  return Math.round(score / values.length);
}

/** 把 Sparkline 历史往前推一个点 */
function pushSparkline(prev: number[], next: number): number[] {
  const nextArr = [...prev, next];
  return nextArr.slice(-12);
}

/** 计算趋势 delta（百分比） */
function computeDelta(prev: number, next: number): number {
  if (prev === 0) return next === 0 ? 0 : 100;
  return Number((((next - prev) / prev) * 100).toFixed(1));
}

/** v6.3.24 由活动总线事件映射为 ActivityItem */
function streamEventToActivity(e: StreamEvent): ActivityItem {
  let type: ActivityEventType = "system.info";
  if (e.type === "system.ready") type = "system.ready";
  else if (e.type === "workflow.started") type = "workflow.started";
  else if (e.type === "workflow.completed") type = "workflow.completed";
  else if (e.type === "workflow.failed") type = "workflow.failed";
  else if (e.type === "content.generated" || e.type === "content.published") type = "system.info";
  else if (e.type === "knowledge.recorded") type = "knowledge.query";

  let status: ActivityStatus = "info";
  if (e.status === "success") status = "success";
  else if (e.status === "failed") status = "failed";
  else if (e.status === "running") status = "running";
  else if (e.status === "warning") status = "warning";

  return {
    id: e.id,
    type,
    status,
    summary: e.summary,
    timestamp: e.timestamp,
    source: (e.source as RuntimeName) || undefined,
  };
}

/** 由 WorkflowExecution 列表派生活动流 */
function executionsToActivities(
  executions: WorkflowExecution[]
): ActivityItem[] {
  const sorted = [...executions].sort(
    (a, b) =>
      new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
  );
  return sorted.slice(0, 20).map((e) => {
    const isRunning = e.status === WorkflowStatus.Running;
    const isFailed = e.status === WorkflowStatus.Failed;
    const isCompleted = e.status === WorkflowStatus.Completed;
    const status: ActivityStatus = isRunning
      ? "running"
      : isFailed
      ? "failed"
      : isCompleted
      ? "success"
      : "warning";
    const type: ActivityEventType = isRunning
      ? "workflow.started"
      : isFailed
      ? "workflow.failed"
      : "workflow.completed";
    return {
      id: e.executionId,
      type,
      status,
      summary: `${e.workflowName} → ${e.status}`,
      timestamp: e.finishedAt || e.startedAt,
      source: RuntimeName.Workflow,
    };
  });
}

/** 由 Scheduler 任务列表派生待办 */
function tasksToTodos(tasks: ScheduledTask[]): TodoItem[] {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const end = start + 24 * 60 * 60 * 1000;
  return tasks
    .filter((t) => {
      if (!t.enabled) return false;
      if (!t.nextRunAt) return true;
      const ts = new Date(t.nextRunAt).getTime();
      return ts >= start && ts < end;
    })
    .slice(0, 8)
    .map((t) => ({
      id: t.id,
      title: t.name,
      time: t.nextRunAt || new Date().toISOString(),
      priority: t.type === "cron" ? "medium" : "high",
      completed: false,
    }));
}

// ============================================
// Hook 实现
// ============================================

export function useCockpitData(): CockpitData {
  const [data, setData] = useState<CockpitData>({
    kpi: emptyKpi(),
    runtimes: emptyRuntimes(),
    activities: emptyActivities(),
    workflowDag: null,
    performance: emptyPerformance(),
    todos: emptyTodos(),
    degraded: true,
    loading: true,
    lastUpdated: null,
  });

  // 用 ref 保存上一次 KPI，用于计算趋势
  const prevKpiRef = useRef<KpiData | null>(null);
  // 用 ref 保存 sparkline 历史
  const sparklineRef = useRef<Record<keyof KpiData["trends"], number[]>>({
    activeWorkflows: [],
    successRate: [],
    avgDuration: [],
    healthScore: [],
  });

  /** 拉取 KPI（3s） */
  const fetchKpi = async () => {
    try {
      const statusSummary = RuntimeManager.getStatusSummary();
      const execResp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.ListExecutions,
      });
      const executions = unwrapArray<WorkflowExecution>(execResp);

      const activeWorkflows = executions.filter(
        (e) => e.status === WorkflowStatus.Running
      ).length;
      const completed = executions.filter(
        (e) => e.status === WorkflowStatus.Completed
      ).length;
      const failed = executions.filter(
        (e) => e.status === WorkflowStatus.Failed
      ).length;
      const total = completed + failed;
      const successRate = total === 0 ? 100 : Number(((completed / total) * 100).toFixed(1));
      const finishedExecs = executions.filter((e) => e.finishedAt && e.startedAt);
      const durations = finishedExecs.map(
        (e) => new Date(e.finishedAt!).getTime() - new Date(e.startedAt).getTime()
      );
      const avgDuration =
        durations.length === 0
          ? 0
          : Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
      const healthScore = computeHealthScore(statusSummary.health);

      const prev = prevKpiRef.current;
      const prevSpark = sparklineRef.current;

      const nextSpark = {
        activeWorkflows: pushSparkline(prevSpark.activeWorkflows, activeWorkflows),
        successRate: pushSparkline(prevSpark.successRate, successRate),
        avgDuration: pushSparkline(prevSpark.avgDuration, avgDuration),
        healthScore: pushSparkline(prevSpark.healthScore, healthScore),
      };
      sparklineRef.current = nextSpark;

      const kpi: KpiData = {
        activeWorkflows,
        successRate,
        avgDuration,
        healthScore,
        trends: {
          activeWorkflows: {
            delta: prev ? computeDelta(prev.activeWorkflows, activeWorkflows) : 0,
            sparkline: nextSpark.activeWorkflows,
          },
          successRate: {
            delta: prev ? computeDelta(prev.successRate, successRate) : 0,
            sparkline: nextSpark.successRate,
          },
          avgDuration: {
            delta: prev ? computeDelta(prev.avgDuration, avgDuration) : 0,
            sparkline: nextSpark.avgDuration,
          },
          healthScore: {
            delta: prev ? computeDelta(prev.healthScore, healthScore) : 0,
            sparkline: nextSpark.healthScore,
          },
        },
      };
      prevKpiRef.current = kpi;
      return kpi;
    } catch (err) {
      console.warn("[DEGRADED] KPI 拉取失败，返回空数据", err);
      return emptyKpi();
    }
  };

  /** 拉取 Runtimes（30s） */
  const fetchRuntimes = (): RuntimeTile[] => {
    try {
      const summary = RuntimeManager.getStatusSummary();
      const latency = RuntimeManager.getLatencyStats();
      const tiles: RuntimeTile[] = RUNTIME_ORDER.map((name) => {
        const status = summary.status[name] || ("idle" as RuntimeStatusType);
        const health = summary.health[name] || ("unknown" as RuntimeHealth);
        const lat = latency.get(name);
        return {
          name,
          status,
          health,
          version: "1.0.0",
          calls: lat?.count ?? 0,
          avgLatency: lat?.avg ? Math.round(lat.avg) : 0,
          uptime: summary.uptime,
        };
      });
      return tiles;
    } catch (err) {
      console.warn("[DEGRADED] Runtimes 拉取失败，返回空数据", err);
      return emptyRuntimes();
    }
  };

  /** 拉取活动流（5s） */
  const fetchActivities = async (): Promise<ActivityItem[]> => {
    try {
      const execResp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.ListExecutions,
      });
      const executions = unwrapArray<WorkflowExecution>(execResp);

      // v6.3.24 持久化执行记录：WorkflowMemory 是内存存储（刷新页面即清空），
      // 这里顺带把最近 50 条执行写入 localStorage，保证实时流刷新后仍有历史
      if (executions.length > 0) {
        try {
          localStorage.setItem(
            "xeaos_workflow_executions",
            JSON.stringify(executions.slice(0, 50))
          );
        } catch {
          // 持久化失败不影响实时流
        }
      }

      // v6.3.24 合并活动总线事件（系统就绪等真实事件）+ 工作流执行事件，按时间倒序取最近 20 条
      const sysActivities = activityBus.list(20).map(streamEventToActivity);
      const wfActivities = executionsToActivities(executions);
      return [...sysActivities, ...wfActivities]
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )
        .slice(0, 20);
    } catch (err) {
      console.warn("[DEGRADED] Activities 拉取失败，返回空数据", err);
      return emptyActivities();
    }
  };

  /** 拉取最近工作流 DAG（30s） */
  const fetchWorkflowDag = async (): Promise<WorkflowExecution | null> => {
    try {
      const execResp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.ListExecutions,
      });
      const executions = unwrapArray<WorkflowExecution>(execResp);
      if (executions.length === 0) return null;
      const sorted = [...executions].sort(
        (a, b) =>
          new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
      );
      return sorted[0];
    } catch (err) {
      console.warn("[DEGRADED] WorkflowDag 拉取失败", err);
      return null;
    }
  };

  /** 拉取性能数据（5s） */
  const fetchPerformance = async (
    prev: PerformancePoint[]
  ): Promise<PerformancePoint[]> => {
    try {
      const execResp = await RuntimeManager.query(RuntimeName.Workflow, {
        type: WorkflowQuery.ListExecutions,
      });
      const executions = unwrapArray<WorkflowExecution>(execResp);
      // 取最近一条执行的时长作为新数据点
      const finishedExecs = executions
        .filter((e) => e.finishedAt && e.startedAt)
        .sort(
          (a, b) =>
            new Date(b.finishedAt!).getTime() -
            new Date(a.finishedAt!).getTime()
        );
      const next: PerformancePoint = (() => {
        const t = new Date();
        const label = `${String(t.getHours()).padStart(2, "0")}:${String(
          t.getMinutes()
        ).padStart(2, "0")}`;
        if (finishedExecs.length === 0) {
          return { time: t.toISOString(), label, success: 0, failed: 0 };
        }
        const latest = finishedExecs[0];
        const duration =
          new Date(latest.finishedAt!).getTime() -
          new Date(latest.startedAt).getTime();
        const isFail = latest.status === WorkflowStatus.Failed;
        return {
          time: t.toISOString(),
          label,
          success: isFail ? 0 : duration,
          failed: isFail ? duration : 0,
        };
      })();
      // 追加新点，保留最近 60 个
      const merged = [...(prev.length > 0 ? prev : emptyPerformance()), next];
      return merged.slice(-60);
    } catch (err) {
      console.warn("[DEGRADED] Performance 拉取失败", err);
      return prev.length > 0 ? prev : emptyPerformance();
    }
  };

  /** 拉取待办（30s） */
  const fetchTodos = async (): Promise<TodoItem[]> => {
    try {
      const resp = await RuntimeManager.query(RuntimeName.Scheduler, {
        type: SchedulerQuery.List,
      });
      const tasks = unwrapArray<ScheduledTask>(resp);
      if (tasks.length === 0) {
        return emptyTodos();
      }
      const todos = tasksToTodos(tasks);
      return todos.length > 0 ? todos : emptyTodos();
    } catch (err) {
      console.warn("[DEGRADED] Todos 拉取失败，返回空数据", err);
      return emptyTodos();
    }
  };

  /** 首次全量加载 */
  const loadAll = async () => {
    const [kpi, activities, workflowDag, todos] = await Promise.all([
      fetchKpi(),
      fetchActivities(),
      fetchWorkflowDag(),
      fetchTodos(),
    ]);
    const runtimes = fetchRuntimes();
    const performance = await fetchPerformance([]);
    setData({
      kpi,
      runtimes,
      activities,
      workflowDag,
      performance,
      todos,
      degraded: false,
      loading: false,
      lastUpdated: new Date().toISOString(),
    });
  };

  /** 首次加载 */
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** KPI 轮询：3s */
  useEffect(() => {
    const timer = setInterval(async () => {
      const kpi = await fetchKpi();
      setData((prev) => ({ ...prev, kpi, lastUpdated: new Date().toISOString() }));
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  /** 活动流轮询：5s */
  useEffect(() => {
    const timer = setInterval(async () => {
      const activities = await fetchActivities();
      setData((prev) => ({
        ...prev,
        activities,
        lastUpdated: new Date().toISOString(),
      }));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  /** 性能轮询：5s */
  useEffect(() => {
    const timer = setInterval(async () => {
      const performance = await fetchPerformance(data.performance);
      setData((prev) => ({
        ...prev,
        performance,
        lastUpdated: new Date().toISOString(),
      }));
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Runtimes / WorkflowDag / Todos 轮询：30s */
  useEffect(() => {
    const timer = setInterval(async () => {
      const [workflowDag, todos] = await Promise.all([
        fetchWorkflowDag(),
        fetchTodos(),
      ]);
      const runtimes = fetchRuntimes();
      setData((prev) => ({
        ...prev,
        runtimes,
        workflowDag,
        todos,
        lastUpdated: new Date().toISOString(),
      }));
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  return data;
}

export default useCockpitData;
